/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterChipGroup, OperationalFilterFieldLabel, OperationalFilterPickerRow, OperationalFilterSegmentedRow } from '../../shared/filters/OperationalFilterPrimitives';
import {
  SORT_OPTIONS,
  STATUS_FILTER_OPTIONS,
  UTILIZATION_FILTER_OPTIONS,
  DATE_PRESET_OPTIONS,
  type CustomerSortBy,
  type CustomerStatusFilter,
  type CustomerUtilizationFilter,
  type CustomerDatePreset,
} from '../screens/hooks/useCustomerListFlow';

export type CustomerFilterDraft = {
  statusFilter: CustomerStatusFilter;
  sortBy: CustomerSortBy;
  utilizationFilter: CustomerUtilizationFilter;
  datePreset: CustomerDatePreset;
};

export type CustomerFilterSheetContentProps = {
  draft: CustomerFilterDraft;
  setDraft: (updater: (prev: CustomerFilterDraft) => CustomerFilterDraft) => void;
};

export function CustomerFilterSheetContent({
  draft,
  setDraft,
}: CustomerFilterSheetContentProps) {
  const [sortSheetOpen, setSortSheetOpen] = useState(false);

  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((o) => o.value === draft.sortBy)?.label ?? 'Última actividad',
    [draft.sortBy]
  );

  const sortSheetOptions = useMemo(
    () => SORT_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  return (
    <View style={styles.container}>
      <View style={styles.section}>
        <OperationalFilterFieldLabel>Estado</OperationalFilterFieldLabel>
        <OperationalFilterChipGroup
          ticketTone
          options={STATUS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={draft.statusFilter}
          onChange={(statusFilter) => setDraft((p) => ({ ...p, statusFilter }))}
        />
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Uso del crédito</OperationalFilterFieldLabel>
        <OperationalFilterChipGroup
          ticketTone
          options={UTILIZATION_FILTER_OPTIONS.map((o) => ({
            value: o.value,
            label: o.value === 'near_limit' ? 'Cerca del límite' : o.label,
          }))}
          value={draft.utilizationFilter}
          onChange={(utilizationFilter) => setDraft((p) => ({ ...p, utilizationFilter }))}
        />
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Actividad</OperationalFilterFieldLabel>
        <OperationalFilterSegmentedRow
          ticketTone
          options={DATE_PRESET_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          value={draft.datePreset}
          onChange={(datePreset) => setDraft((p) => ({ ...p, datePreset }))}
        />
      </View>

      <View style={styles.section}>
        <OperationalFilterFieldLabel>Orden</OperationalFilterFieldLabel>
        <OperationalFilterPickerRow
          layout="stack"
          label="Ordenar por"
          valueLabel={sortLabel}
          active={draft.sortBy !== 'updated_desc'}
          onPress={() => setSortSheetOpen(true)}
          accessibilityLabel={`Ordenar por, actual: ${sortLabel}`}
        />
      </View>

      <MasterFilterSheetModal
        visible={sortSheetOpen}
        title="Ordenar clientes"
        options={sortSheetOptions}
        selectedId={draft.sortBy}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          setDraft((p) => ({ ...p, sortBy: (id as CustomerSortBy) ?? 'updated_desc' }));
          setSortSheetOpen(false);
        }}
        onClose={() => setSortSheetOpen(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 18,
  },
  section: {
    gap: 8,
  },
});
